import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CalendarX, Clock, ArrowsLeftRight, CheckCircle, Circle } from "@phosphor-icons/react";
import { todayLocalDateString } from "@/lib/localDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin dialog to reschedule a match.
 * Shows free vs occupied slots for the selected date, lets admin:
 *   - pick a free slot,
 *   - swap with an existing match on the same slot,
 *   - or postpone (clear the schedule for rescheduling later).
 */
export default function RescheduleMatchDialog({
  open, onOpenChange, match, tournamentId, onChanged,
}) {
  const [date, setDate] = useState("");
  const [slotsData, setSlotsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open || !match) return;
    setDate(match.scheduled_date || todayLocalDateString());
    setSlotsData(null);
  }, [open, match]);

  const fetchSlots = useCallback(async () => {
    if (!date || !tournamentId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/tournaments/${tournamentId}/free-slots`,
        { params: { date, best_of: match?.best_of }, withCredentials: true }
      );
      setSlotsData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load slots");
    } finally { setLoading(false); }
  }, [date, tournamentId, match?.best_of]);

  useEffect(() => { if (open) fetchSlots(); }, [fetchSlots, open]);

  const applySlot = async (venueId, tableName, time, existingMatch) => {
    if (!match) return;
    setWorking(true);
    try {
      if (existingMatch) {
        // Swap path
        await axios.put(`${API}/matches/${match.id}/reschedule`,
          { swap_with_match_id: existingMatch.match_id },
          { withCredentials: true });
        toast.success(`Swapped with ${existingMatch.player1_name} vs ${existingMatch.player2_name}`);
      } else {
        await axios.put(`${API}/matches/${match.id}/reschedule`,
          { scheduled_date: date, scheduled_time: time, table_name: tableName, venue_id: venueId },
          { withCredentials: true });
        toast.success(`Rescheduled to ${date} ${time} on ${tableName}`);
      }
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    } finally { setWorking(false); }
  };

  const postpone = async () => {
    if (!match) return;
    if (!window.confirm("Postpone this match? Date/time/table will be cleared.")) return;
    setWorking(true);
    try {
      await axios.post(`${API}/matches/${match.id}/postpone`, {}, { withCredentials: true });
      toast.success("Match postponed");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Postpone failed");
    } finally { setWorking(false); }
  };

  if (!match) return null;
  const thisMatchId = match.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" weight="fill" />
            Reschedule Match
          </DialogTitle>
          <DialogDescription>
            <strong>{match.player1_name || "P1"}</strong> vs <strong>{match.player2_name || "P2"}</strong>
            {" · "}Currently: {match.scheduled_date || "Unscheduled"}
            {match.scheduled_time ? ` at ${match.scheduled_time}` : ""}
            {match.table_name ? ` · ${match.table_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs">Date</Label>
              <Input
                type="date" value={date}
                onChange={e => setDate(e.target.value)}
                data-testid="reschedule-date-input"
              />
            </div>
            <Button variant="outline" onClick={fetchSlots} disabled={loading} data-testid="reload-slots-btn">
              {loading ? "Loading…" : "Refresh Slots"}
            </Button>
            <Button variant="destructive" onClick={postpone} disabled={working} data-testid="postpone-btn">
              <CalendarX className="w-4 h-4 mr-1" />Postpone
            </Button>
          </div>

          {loading && (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading slots…</div>
          )}

          {!loading && slotsData && slotsData.venues?.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">No venues configured.</div>
          )}

          {!loading && slotsData && slotsData.venues?.map(venue => (
            <div key={venue.venue_id} className="space-y-2">
              <p className="text-sm font-semibold">{venue.venue_name}</p>
              <div className="space-y-3">
                {venue.tables.map(table => (
                  <div key={table.table_name} className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{table.table_name}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`slots-${venue.venue_id}-${table.table_name}`}>
                      {table.slots.map(slot => {
                        const isThisMatch = slot.match?.match_id === thisMatchId;
                        const canSwap = slot.occupied && !isThisMatch;
                        return (
                          <button
                            key={slot.time}
                            type="button"
                            disabled={working || isThisMatch}
                            onClick={() => applySlot(venue.venue_id, table.table_name, slot.time, canSwap ? slot.match : null)}
                            className={`text-left p-3 rounded-lg border transition-colors ${
                              isThisMatch
                                ? "border-primary/60 bg-primary/15 cursor-default"
                                : slot.occupied
                                  ? "border-secondary/30 bg-secondary/5 hover:bg-secondary/10"
                                  : "border-border hover:bg-muted/20 hover:border-primary/40"
                            }`}
                            data-testid={`slot-${venue.venue_id}-${table.table_name}-${slot.time}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-heading font-bold text-sm flex items-center gap-1.5">
                                {slot.occupied
                                  ? <CheckCircle className="w-3.5 h-3.5 text-secondary" weight="fill" />
                                  : <Circle className="w-3.5 h-3.5 text-muted-foreground" />}
                                {slot.time}
                              </span>
                              <Badge variant="outline" className="text-[9px]">{slot.duration_mins}m</Badge>
                            </div>
                            {isThisMatch ? (
                              <p className="text-[11px] text-primary">Current slot for this match</p>
                            ) : slot.occupied ? (
                              <div>
                                <p className="text-[11px] font-medium truncate">
                                  {slot.match.player1_name} vs {slot.match.player2_name}
                                </p>
                                <p className="text-[10px] text-secondary flex items-center gap-1 mt-0.5">
                                  <ArrowsLeftRight className="w-3 h-3" />Click to swap
                                </p>
                              </div>
                            ) : (
                              <p className="text-[11px] text-muted-foreground">Free — click to assign</p>
                            )}
                          </button>
                        );
                      })}
                      {table.slots.length === 0 && (
                        <p className="text-xs text-muted-foreground col-span-full">
                          No slots available on this date (table schedule may be closed).
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
